import { fetchDeliveryInvoicePrefill } from '../../orders/api/supplierOrderDeliveryApi';
import InvoiceOrderAlignment from './InvoiceOrderAlignment';
import InvoiceAdvanceBalance from './InvoiceAdvanceBalance';
import PurchaseInvoiceReceipt from './PurchaseInvoiceReceipt';
import PurchaseReceiptPrompt from './PurchaseReceiptPrompt';
import InvoicePurchaseFlow from './InvoicePurchaseFlow';
import InvoiceCorrectionsPanel from './InvoiceCorrectionsPanel';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Card, Collapse, DatePicker, Dropdown, Form, Popconfirm, Space, Spin, Tag, Typography, type MenuProps } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, FileAddOutlined, FileSearchOutlined, FileTextOutlined, IdcardOutlined, MoreOutlined, PaperClipOutlined, ScanOutlined, ShopOutlined, SwapOutlined, UploadOutlined } from '@ant-design/icons';
import { ObjectCardEmpty, ObjectCardHero, ObjectCardSection } from '../../../components/layout/ObjectCard';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import WorkflowInvoiceEditor from './WorkflowInvoiceEditor';
import { formatAmountGrouped, formatCurrency } from '../../../utils/currency';
import InvoiceDraftSummary, { InvoiceCardLines, invoiceCardFacts } from './InvoiceDraftSummary';
import InvoiceEditorModal from './InvoiceEditorModal';
import InvoiceOrderPicker from './InvoiceOrderPicker';
import InvoiceApprovalPreview from './InvoiceApprovalPreview';
import { invoiceWorkflowApi, fetchInvoiceApprovalPreview, fetchInvoiceCorrectionState, type InvoiceApprovalPreview as ApprovalPreview, type InvoiceApprovalInput, type InvoiceApprovalResult } from '../api/invoiceWorkflowApi';
import { getInvoiceImportErrorMessage, isBlockingInvoiceImportError } from '../invoiceImportErrorMessages';
import { mergeInvoiceSourceMetadata } from '../invoiceDraftState';
import { RECOGNITION_MONTH_FORMAT, recognitionMonthLabel, recognitionMonthMismatch, withDefaultRecognitionMonth } from '../invoiceRecognitionMonth';
import { validateWorkflowInvoiceForApproval } from '../workflowInvoiceValidation';
import { quantityGap, quantityGapKey, withDimensionQuantity } from '../workflowInvoiceQuantity';
import { confirmQuantityGaps } from './confirmQuantityGaps';
import { initialSalesInvoiceKind, isAmountInvoice, isSalesProforma, type WorkflowInvoice, type WorkflowInvoiceDirection, type WorkflowInvoiceSourceLineCandidate } from '../../../types/workflowInvoice';
import { useFeedback } from '../../../utils/feedback';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import { useBackNavigation } from '../../../hooks/useBackNavigation';
import '../../../components/layout/DocumentCard.css';
import './InvoiceDraftDetail.css';
import './InvoiceDraftSummary.css';
import './InvoiceEditorModal.css';

interface InvoiceDraftDetailProps {
  direction: WorkflowInvoiceDirection;
  embedded?: {
    invoiceId: string;
    supplierOrderId: string;
    transportId?: string;
    receiptOnly?: boolean;
    /** PZ for one truck of a collective invoice: what each ZD line of that truck still awaits. */
    truckOpenQuantities?: Record<string, number>;
    onClose: () => void;
    onPersisted: () => void;
  };
}

export default function InvoiceDraftDetail({ direction, embedded }: InvoiceDraftDetailProps) {
  const purchaseFlow = direction === 'PURCHASE';
  const { id: routeId } = useParams<{ id: string }>();
  const [embeddedId, setEmbeddedId] = useState(embedded?.invoiceId);
  const id = embedded ? embeddedId : routeId;
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { t } = useTranslation(['invoices', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const commands = useMemo(() => invoiceWorkflowApi(direction), [direction]);
  const base = direction === 'PURCHASE' ? '/app/purchase-invoices' : '/app/sales-invoices';
  const isNew = id === 'new';
  const [draft, setDraft] = useState<WorkflowInvoice | null>(null);
  const current = useRef<WorkflowInvoice | null>(null);
  const revision = useRef(0);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [candidates, setCandidates] = useState<WorkflowInvoiceSourceLineCandidate[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [alignmentAccepted,setAlignmentAccepted] = useState<string | null>(null);
  const [preview, setPreview] = useState<ApprovalPreview | null>(null);
  const [previewRevision, setPreviewRevision] = useState(0);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [approval, setApproval] = useState<InvoiceApprovalResult | null>(null);
  // Details editor lives in a same-route modal. Closing it (button, X or ESC)
  // never discards: the page draft (and its unsaved indicator) retains every
  // local edit, which survives closing but not a browser reload.
  const [editorOpen, setEditorOpen] = useState(false);
  const [purchaseStep, setPurchaseStep] = useState(0);
  // WP7: "Utworzyć PZ automatycznie?" opens only right after this session's
  // purchase approval; a fallback leaves a notice above the receipt panel.
  const [receiptPromptOpen, setReceiptPromptOpen] = useState(false);
  const [receiptNotice, setReceiptNotice] = useState<string | null>(null);
  const [receiptRevision, setReceiptRevision] = useState(0);
  const receiptPanel = useRef<HTMLDivElement>(null);
  const approvalKey = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const requestGeneration = useRef(0);
  const draftSession = useRef(0);
  const previousRoute = useRef({ id, direction });
  // Route identity adoption keeps the session; leaving the component does not.
  useEffect(() => () => { draftSession.current += 1; }, []);
  // Owner decision 2026-10-13: until the operator picks a month, the
  // recognition month follows the invoice issue date (also after OCR/edits).
  const monthChosen = useRef(false);
  // FZ.2: gaps already saved (loaded or confirmed in the pop-up) are not asked again.
  const answeredGaps = useRef(new Set<string>());
  const savedGapKeys = (lines: WorkflowInvoice['lines']) => lines
    .filter(line => line.sourceOrderLineId && quantityGap(line)).map(quantityGapKey);
  const replace = (raw: WorkflowInvoice) => { const next = withDefaultRecognitionMonth(raw, monthChosen.current); current.current = next; setDraft(next); };
  const choosePeriod = (settlementPeriod: string | null) => {
    monthChosen.current = Boolean(settlementPeriod);
    if (current.current) replace({ ...current.current, settlementPeriod });
  };
  const edit = (next: WorkflowInvoice) => { revision.current += 1; setDirty(true); replace(next); };
  const error = (cause: any) => {
    if (cause?.response?.data?.code === 'VERSION_CONFLICT') setConflict(true);
    feedback.error(getInvoiceImportErrorMessage(cause, t, 'invoices:error.updateFailed'), { sticky: isBlockingInvoiceImportError(cause) });
  };
  // Busy flags belong to the draft session that started the request. A late
  // completion from a previous route must never clear the new draft's state.
  const finishBusy = (session: number) => { if (session === draftSession.current) setBusy(false); };
  const finishExtracting = (session: number) => { if (session === draftSession.current) setExtracting(false); };
  useEffect(() => {
    const previous = previousRoute.current;
    previousRoute.current = { id, direction };
    // Creating a draft adopts its server identity without loading over the
    // editor. This also preserves edits made while create/upload was pending.
    if (previous.id === 'new' && previous.direction === direction && id !== 'new'
      && current.current?.id === id && current.current?.direction === direction) return;
    draftSession.current += 1;
    const generation = ++requestGeneration.current;
    setLoading(true); setDirty(false); setConflict(false); setApproval(null); approvalKey.current = null; revision.current = 0; monthChosen.current = false;
    // A real route switch owns fresh progress state: a late previous upload
    // or save must not leave the new draft's actions disabled.
    setBusy(false); setExtracting(false);
    setEditorOpen(false);
    setReceiptPromptOpen(false); setReceiptNotice(null);
    const search = new URLSearchParams(window.location.search);
    const initialSource = embedded?.supplierOrderId ?? search.get(direction === 'PURCHASE' ? 'supplierOrderId' : 'orderId');
    const initialKind = direction === 'SALES' ? initialSalesInvoiceKind(search.get('kind')) : 'STANDARD';
    const request = isNew && embedded?.transportId ? fetchDeliveryInvoicePrefill(embedded.supplierOrderId, embedded.transportId) : isNew ? Promise.resolve<WorkflowInvoice>({ direction, kind: initialKind, status: 'DRAFT', extractionStatus: 'IDLE', lines: [], linesLoaded: true, manuallyCorrected: false, ...(direction === 'PURCHASE' ? { supplierOrderId: initialSource } : { orderId: initialSource }) }) : commands.fetch(id!);
    request.then(rawValue => {
      if (generation !== requestGeneration.current) return;
      replace(rawValue);
      const value = current.current!;
      answeredGaps.current = new Set(isNew ? [] : savedGapKeys(value.lines));
      setPurchaseStep(value.id || value.sourceTransportId ? 1 : 0);
      if (isNew && value.sourceTransportId) { setEditorOpen(true); setDirty(true); }
      if (value.approvalResult) setApproval({ ...value.approvalResult, invoice: value });
      const source = direction === 'PURCHASE' ? value.supplierOrderId : value.orderId;
      setSourceIds(source ? [source] : []);
    }).catch(error).finally(() => { if (generation === requestGeneration.current) setLoading(false); });
    return () => { requestGeneration.current += 1; };
    // A route change loads once; feedback/i18n identities must not reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, direction]);

  useEffect(() => {
    if (!draft?.id) return;
    let active = true;
    setPreview(null); setAlignmentAccepted(null); setPreviewFailed(false); approvalKey.current = null;
    fetchInvoiceApprovalPreview(direction, draft.id).then(result => {
      if (!active) return;
      setPreview(result);
      if (direction === 'PURCHASE') setSourceIds(previous => Array.from(new Set([...previous, ...result.orders.map(order => order.id)])));
    }).catch(() => { if (active) setPreviewFailed(true); });
    return () => { active = false; };
  }, [draft?.id, draft?.version, direction, previewRevision]);
  // Routed card header: a withdrawn invoice (WP2) shows a „Wycofana” pill.
  // Read from the same corrections endpoint the corrections panel uses.
  const [withdrawn, setWithdrawn] = useState(false);
  useEffect(() => {
    setWithdrawn(false);
    if (embedded || !draft?.id || draft.status !== 'APPROVED') return;
    let active = true;
    Promise.resolve().then(() => fetchInvoiceCorrectionState(direction, draft.id!))
      .then(state => { if (active) setWithdrawn(Boolean(state && !state.isCorrection && state.withdrawal)); })
      .catch(() => { /* the corrections panel reports a failed load */ });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id, draft?.version, draft?.status, direction, previewRevision]);


  // onGapCancel: a caller that started more than the save (upload) says why it stopped.
  const save = async ({ onGapCancel }: { onGapCancel?: () => void } = {}): Promise<WorkflowInvoice | null> => {
    const rawValue = current.current;
    if (!rawValue) return null;
    // A new draft adopts its server id without re-running the route loader.
    // Resolve the month at the command boundary so that this path has the same
    // default as an already persisted draft, while preserving an operator's
    // explicit month choice.
    let value = withDefaultRecognitionMonth(rawValue, monthChosen.current);
    // File-first OCR: drafts persist without a currency so a source file can
    // be attached and read before the operator picks one. Currency stays
    // strictly required at approval via validateWorkflowInvoiceForApproval.
    if (!value.linesLoaded) { feedback.error(t('invoices:workflow.validation.linesRequired')); return null; }
    // FZ.2: an amount to pay that differs from the line's whole pieces is saved
    // only after the operator picks it or the amount from the dimensions.
    const gaps = value.lines.map((line, index) => ({ position: index + 1, line }))
      .filter(({ line }) => line.sourceOrderLineId && quantityGap(line) && !answeredGaps.current.has(quantityGapKey(line)));
    if (gaps.length) {
      const askedSession = draftSession.current;
      const choice = await confirmQuantityGaps(modal, t, gaps);
      if (askedSession !== draftSession.current) return null;
      if (!choice) { onGapCancel?.(); return null; }
      if (choice === 'dimensions') {
        const netPrices = value.direction === 'PURCHASE' && value.netPurchasePrices === true;
        const keys = new Set(gaps.map(({ line }) => line.key));
        // Part of this save, not a new edit: the caller's revision stays valid.
        replace({ ...value, lines: value.lines.map(line => keys.has(line.key) ? withDimensionQuantity(line, netPrices) : line) }); setDirty(true);
        value = current.current!;
      }
    }
    const capturedRevision = revision.current;
    const session = draftSession.current;
    const updated = await submitWithOperationalException(
      payload => value.id
        ? commands.update(value.id, payload, value.version!)
        : commands.create(payload),
      commands.payload(value), modal, t,
    );
    if (!updated) return null;
    if (session !== draftSession.current) return null;
    savedGapKeys(updated.lines).forEach(key => answeredGaps.current.add(key));
    if (revision.current === capturedRevision) { replace({ ...updated, settlementPeriod: value.settlementPeriod }); setDirty(false); }
    else if (current.current) replace(mergeInvoiceSourceMetadata(current.current, updated));
    if (!value.id && updated.id) {
      if (embedded) setEmbeddedId(updated.id);
      else navigate(`${base}/${updated.id}`, { replace: true });
    }
    embedded?.onPersisted();
    return updated;
  };
  // Modal save closes only on a clean success: a failure, a conflict, an
  // in-flight extraction, edits made during the save, or a route switch all
  // keep the current state untouched instead of closing over it.
  const handleModalSave = async () => {
    if (conflict || extracting) return;
    const session = draftSession.current;
    const capturedRevision = revision.current;
    setBusy(true);
    try {
      const updated = await save();
      if (!updated) return;
      if (session !== draftSession.current) return;
      if (revision.current !== capturedRevision) return;
      feedback.success(t('invoices:success.updated'));
      setEditorOpen(false);
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishBusy(session); }
  };
  const upload = async (files: File[]) => {
    if (!files.length || conflict) return;
    const session = draftSession.current;
    setBusy(true);
    try {
      let value = current.current;
      if (!value?.id) value = await save({ onGapCancel: () => feedback.info(t('invoices:workflow.quantityGap.uploadCancelled')) });
      if (!value?.id) return;
      for (const file of files) {
        if (session !== draftSession.current) return;
        const metadata = await commands.attach(value.id, file);
        if (session !== draftSession.current) return;
        if (current.current) replace(mergeInvoiceSourceMetadata(current.current, metadata));
      }
      if (session !== draftSession.current) return;
      feedback.success(t('invoices:workflow.page.uploaded'));
      // Both directions read the uploaded file at once (sales used to wait for
      // a separate „Odczytaj” click, which looked like OCR not working).
      if (purchaseFlow) setPurchaseStep(1);
      await extract();
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishBusy(session); }
  };
  const apply = async (overwrite: boolean) => {
    const value = current.current;
    if (!value?.id || !value.extractionSeq || !value.version || conflict) return;
    const session = draftSession.current;
    setBusy(true);
    try {
      const updated = await commands.apply(value.id, { seq: value.extractionSeq, expectedVersion: value.version, overwriteManualEdits: overwrite });
      // Never merge an apply response into a draft the operator already left.
      if (session !== draftSession.current) return;
      if (!current.current || current.current.id !== value.id) return;
      replace({ ...updated, settlementPeriod: value.settlementPeriod }); setDirty(false);
      // A successful OCR application reveals the editor for verification.
      if (purchaseFlow) setPurchaseStep(1);
      else setEditorOpen(true);
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishBusy(session); }
  };
  const confirmApply = () => {
    if (conflict) return;
    if (dirty || current.current?.manuallyCorrected) {
      modal.confirm({ title: t('invoices:workflow.page.applyOverwriteConfirm'), content: t('invoices:workflow.page.editsRetained'), okText: t('invoices:workflow.page.applyExtraction'), cancelText: t('common:cancel'), onOk: () => { void apply(true); } });
    } else void apply(false);
  };
  const extract = async () => {
    const value = current.current;
    if (!value?.id || conflict) return;
    const session = draftSession.current;
    const capturedRevision = revision.current;
    const capturedDirty = dirty;
    setExtracting(true);
    try {
      try {
        await commands.extract(value.id, []);
      } catch (cause) {
        // The server still records the FAILED extraction: refresh status
        // metadata for this draft session only, so the UI stops showing a
        // stale status. Manual edits and lines stay intact; never autoapply
        // in the error path. A failing refresh keeps the edits and reports
        // the original error (no unhandled rejection).
        try {
          const metadata = await commands.fetch(value.id);
          if (session === draftSession.current && current.current?.id === value.id) {
            replace(mergeInvoiceSourceMetadata(current.current, metadata));
          }
        } catch {
          // Metadata refresh is best-effort only.
        }
        if (session === draftSession.current) error(cause);
        return;
      }
      const metadata = await commands.fetch(value.id);
      if (session !== draftSession.current) return;
      if (!current.current || current.current.id !== value.id) return;
      replace(mergeInvoiceSourceMetadata(current.current, metadata));
      // Only a pristine editor may accept automatic prefill. Any edit during
      // inference keeps the local value and leaves an explicit apply action.
      if (session === draftSession.current && !capturedDirty && revision.current === capturedRevision && !value.manuallyCorrected) await apply(false);
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishExtracting(session); }
  };
  const approve = async () => {
    const rawValue = current.current;
    if (!rawValue) return;
    // A sales proforma is only saved to its ZK; the card offers no approval.
    if (isSalesProforma(rawValue)) return;
    const value = withDefaultRecognitionMonth(rawValue, monthChosen.current);
    if (value !== rawValue) replace(value);
    if (!value?.settlementPeriod) { feedback.error(t('invoices:workflow.page.periodRequired')); return; }
    if (dirty) { feedback.error(t('invoices:workflow.page.saveBeforeApproval')); return; }
    if (!preview || previewFailed) { feedback.error(t('invoices:workflow.page.previewFailed')); return; }
    if (validateWorkflowInvoiceForApproval(value).length) { feedback.error(t('invoices:workflow.page.resolveIssues')); return; }
    const currentAlignment = preview.purchaseAlignment || preview.salesAlignment;
    if (currentAlignment?.changes.length && alignmentAccepted !== currentAlignment.fingerprint) { feedback.error(t(`invoices:workflow.alignment.${direction === 'PURCHASE' ? 'purchaseRequired' : 'salesRequired'}`)); return; }
    if (preview.approvalIssue) { feedback.error(preview.approvalIssue.message); return; }
    const session = draftSession.current;
    setBusy(true);
    try {
      const saved = dirty || !value.id ? await save() : value;
      if (session !== draftSession.current) return;
      if (!saved?.id || !saved.version) return;
      approvalKey.current ??= `invoice-${saved.id}-${saved.version}-${Date.now()}`;
      const approvalInput: InvoiceApprovalInput = { expectedVersion: saved.version, idempotencyKey: approvalKey.current, recognitionMonth: value.settlementPeriod, ...(alignmentAccepted ? (direction === 'PURCHASE' ? { purchaseAlignmentFingerprint: alignmentAccepted } : { salesAlignmentFingerprint: alignmentAccepted }) : {}) };
      // Approval warnings need an audited reason before the invoice is approved.
      const draftId = saved.id;
      const result = await submitWithOperationalException(
        (payload) => commands.approve(draftId, payload as unknown as InvoiceApprovalInput),
        approvalInput as unknown as Record<string, unknown>,
        modal,
        t,
      );
      if (session !== draftSession.current) return;
      if (!result) return;
      replace(result.invoice); setApproval(result); setDirty(false);
      embedded?.onPersisted();
      window.dispatchEvent(new CustomEvent('invoice-workflow-changed', { detail: { invoiceId: saved.id, orderIds: result.orders.map(order => order.id) } }));
      feedback.success(t('invoices:workflow.page.approved'));
      // Goods are received only on the supplier order card (decision 2026-10-14).
      if (purchaseFlow && embedded && result.invoice.sourceTransportId) { embedded.onClose(); }
      else if (purchaseFlow && embedded && result.invoice.recognizedSupplierOrderId) { setReceiptNotice(null); setReceiptPromptOpen(true); }
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishBusy(session); }
  };
  const downloadFile = (sha256: string) => {
    const value = current.current;
    if (!value?.id) return;
    void commands.download(value.id, sha256).then(blob => { const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = draft?.sourceFiles?.find(file => file.sha256 === sha256)?.name ?? 'faktura'; link.click(); URL.revokeObjectURL(url); }).catch(error);
  };
  const refreshReceipt = () => {
    setReceiptRevision(value => value + 1);
    setPreviewRevision(value => value + 1);
    embedded?.onPersisted();
  };
  const receiptFallback = (message: string) => {
    setReceiptPromptOpen(false);
    setReceiptNotice(message);
    setTimeout(() => receiptPanel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };
  const close = () => {
    if (busy || extracting) return;
    const leave = () => { if (embedded) embedded.onClose(); else goBack(base); };
    if (dirty) modal.confirm({ title: t('invoices:workflow.page.closeUnsavedConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: leave });
    else leave();
  };
  // WP2: open a correction draft / the original on the same screen, and
  // reload after a withdrawal changed the invoice's order.
  const openInvoice = (invoiceId: string) => {
    if (embedded) setEmbeddedId(invoiceId);
    else navigate(`${base}/${invoiceId}`);
  };
  const reloadAfterCorrection = () => {
    const value = current.current;
    if (!value?.id) return;
    void commands.fetch(value.id).then((next) => { replace(withDefaultRecognitionMonth(next)); setPreviewRevision((revisionValue) => revisionValue + 1); }).catch(error);
    embedded?.onPersisted();
  };
  const correctionsPanel = (value: WorkflowInvoice) => (value.id && (value.status === 'APPROVED' || value.correctsInvoiceId))
    ? <InvoiceCorrectionsPanel direction={direction} invoice={value} preview={preview?.correction ?? null}
        onOpen={openInvoice} onChanged={reloadAfterCorrection} locked={dirty || busy || conflict} />
    : null;
  const continueToReceipt = async () => {
    if (busy || extracting || conflict || !current.current || validateWorkflowInvoiceForApproval(current.current).length) return;
    const session = draftSession.current;
    const capturedRevision = revision.current;
    setBusy(true);
    try {
      const saved = dirty || !current.current.id ? await save() : current.current;
      if (saved && session === draftSession.current && capturedRevision === revision.current) setPurchaseStep(2);
    } catch (cause) { if (session === draftSession.current) error(cause); }
    finally { finishBusy(session); }
  };
  if (loading || !draft) return <Space direction="vertical">
    {loading ? <Spin /> : <Alert type="error" message={t('invoices:workflow.api.draftNotFound')} />}
    {embedded && <Button onClick={close}>{t('common:close')}</Button>}
  </Space>;
  if (embedded?.receiptOnly && draft.status === 'APPROVED' && draft.recognizedSupplierOrderId) return <Space direction="vertical" className="invoice-draft-stack">
    <Button onClick={close}>{t('common:close')}</Button>
    <PurchaseInvoiceReceipt invoice={draft} preview={preview} onChanged={embedded.onPersisted}
      truck={embedded.transportId ? { id: embedded.transportId, openQuantities: embedded.truckOpenQuantities } : undefined} />
  </Space>;
  const editable = draft.status === 'DRAFT';
  const filePresent = Boolean(draft.sourceFileName || draft.sourceFiles?.length);
  const showApply = editable && draft.extractionStatus === 'SUCCESS' && draft.extractionSeq !== draft.lastAppliedExtractionSeq;
  const issues = validateWorkflowInvoiceForApproval(draft);
  const persisted = Boolean(draft.id);
  const monthMismatch = editable ? recognitionMonthMismatch(draft.settlementPeriod, draft.issueDate) : null;
  const progressBusy = busy || extracting;
  const triggerUpload = () => fileInput.current?.click();
  const requestExtraction = () => { if (conflict) return; if (filePresent) void extract(); else triggerUpload(); };
  const orderPicker = (
    <InvoiceOrderPicker direction={direction} ids={sourceIds} correction={Boolean(draft.correctsInvoiceId)} disabled={!editable || busy} onCandidates={setCandidates} onLoading={setSourcesLoading} onChange={(ids, supplierId) => {
      setSourceIds(ids);
      edit({ ...draft, ...(direction === 'PURCHASE' ? { supplierOrderId: ids[0] ?? null, supplierId: supplierId ?? null } : { orderId: ids[0] ?? null }), manuallyCorrected: true });
    }} />
  );
  // The same document already in OMS (2026-10-30): a warning on the sales card
  // and in the purchase flow alike, never a block.
  const sameNumberAlert = editable && persisted && Boolean(preview?.sameNumberDocuments?.length)
    ? <Alert key="same-number" type="warning" showIcon data-testid="invoice-same-number"
        message={t('invoices:workflow.sameNumber.title')}
        description={<>
          <ul className="invoice-card__issues">{preview!.sameNumberDocuments!.map(other => <li key={other.id}>
            <Button type="link" onClick={() => navigate(`${base}/${other.id}`)}>{t('invoices:workflow.sameNumber.item', {
              kind: t(`invoices:workflow.kind.${other.documentKind.toLowerCase()}`),
              state: t(`invoices:workflow.page.status.${other.status === 'APPROVED' ? 'approved' : isSalesProforma({ direction, kind: other.documentKind as WorkflowInvoice['kind'] }) ? 'saved' : 'draft'}`),
              order: other.orderNumber ?? t('invoices:workflow.sameNumber.noOrder'),
            })}</Button>
          </li>)}</ul>
          {t('invoices:workflow.sameNumber.hint')}
        </>} />
    : null;
  if (purchaseFlow && editable) {
    const flow = <>
    {correctionsPanel(draft)}
    {sameNumberAlert}
    <InvoicePurchaseFlow financialOnly={preview?.financialOnly} draft={draft} step={purchaseStep} setStep={setPurchaseStep}
      busy={busy} extracting={extracting} conflict={conflict} dirty={dirty} issues={issues}
      showApply={showApply} onApply={confirmApply} onUpload={triggerUpload} onExtract={requestExtraction}
      onSave={() => void handleModalSave()} onContinue={continueToReceipt} onApprove={() => void approve()} onClose={close}
      onPeriod={choosePeriod} periodHint={monthMismatch ? t('invoices:workflow.page.recognitionMonthDiffers', monthMismatch) : undefined} onDownload={downloadFile}
      onDelete={() => modal.confirm({ title: t('invoices:workflow.page.deleteConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: () => commands.remove(draft.id!).then(() => { if (embedded) { embedded.onPersisted(); embedded.onClose(); } else navigate(base); }).catch(error) })}
      onReload={() => modal.confirm({ title: t('invoices:workflow.page.discardConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: () => commands.fetch(draft.id!).then(value => { monthChosen.current = false; replace(value); setDirty(false); setConflict(false); setPurchaseStep(1); }).catch(error) })}
      canReceive={Boolean(preview && !previewFailed && preview.financialOnly && !preview.approvalIssue && (!preview.purchaseAlignment?.changes.length || alignmentAccepted === preview.purchaseAlignment.fingerprint) && !issues.length)}
      orders={orderPicker}
      editor={<WorkflowInvoiceEditor compactReview value={draft} onChange={edit} extractionStatus={draft.extractionStatus}
        extractionError={draft.extractionError} extractionPending={extracting} onRequestExtraction={requestExtraction}
        sourceLineCandidates={candidates} sourceLinesLoading={sourcesLoading} readOnly={progressBusy || conflict} partialCoverage={draft.coverageState === 'PARTIAL'} />}
      receipt={<>
        {previewFailed ? <Alert type="warning" message={t('invoices:workflow.page.previewFailed')} action={<Button onClick={() => setPreviewRevision(value => value + 1)}>{t('common:refresh')}</Button>} /> : !preview ? <Spin /> : <>
          {preview.financialOnly && preview.purchaseAlignment && <InvoiceOrderAlignment scope="purchase" value={preview.purchaseAlignment} accepted={alignmentAccepted === preview.purchaseAlignment.fingerprint} disabled={progressBusy || dirty} onAccept={accepted => { approvalKey.current = null; setAlignmentAccepted(accepted ? preview.purchaseAlignment!.fingerprint : null); }} />}
          <Alert type={preview.approvalIssue ? 'error' : 'info'} showIcon message={preview.approvalIssue?.message || t('invoices:workflow.financialApproval.hint')} />
        </>}
      </>} />
    <input data-testid="invoice-source-input" ref={fileInput} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" hidden onChange={event => { const files = Array.from(event.target.files ?? []); event.target.value = ''; void upload(files); }} />
    </>;
    // Routed page only: filled fields of the 2026-10-19 direction (InvoiceDraftDetail.css).
    return embedded ? flow : <div className="invoice-flow-page">{flow}</div>;
  }
  if (embedded) return <div className="invoice-draft-page"><Space direction="vertical" size={16} className="invoice-draft-stack">
    <div className="invoice-draft-overview" hidden={Boolean(embedded && editorOpen)}>
    <div className="invoice-draft-topbar">
      <Space size={8}>
        <Button icon={embedded ? undefined : <ArrowLeftOutlined />} disabled={progressBusy} onClick={close}>{t(embedded ? 'common:close' : 'common:back')}</Button>
        <Tag>{t(`invoices:workflow.direction.${direction.toLowerCase()}`)}</Tag>
      </Space>
      {editable && persisted && <Popconfirm okText={t('common:yes')} cancelText={t('common:no')} title={t('invoices:workflow.page.deleteConfirm')} onConfirm={() => { void commands.remove(draft.id!).then(() => { if (embedded) { embedded.onPersisted(); embedded.onClose(); } else navigate(base); }).catch(error); }}><Button danger type="text" disabled={progressBusy}>{t('invoices:workflow.page.deleteDraft')}</Button></Popconfirm>}
    </div>
    {conflict && <Alert type="error" showIcon message={t('invoices:workflow.api.versionConflict')} description={t('invoices:workflow.page.conflictRetained')} action={<Button onClick={() => modal.confirm({ title: t('invoices:workflow.page.discardConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: () => { void commands.fetch(draft.id!).then(value => { replace(value); setDirty(false); setConflict(false); }).catch(error); } })}>{t('common:refresh')}</Button>} />}
    {!persisted ? (
      <div className="invoice-intake" data-testid="invoice-intake">
        <h2 className="invoice-intake-title">{t('invoices:workflow.summary.intakeTitle')}</h2>
        {dirty && <Tag color="gold" data-testid="invoice-intake-unsaved">{t('invoices:workflow.summary.unsaved')}</Tag>}
        <p className="invoice-intake-hint">{t('invoices:workflow.summary.intakeHint')}</p>
        <div className="invoice-intake-actions">
          <Button type="primary" icon={<UploadOutlined />} disabled={progressBusy} onClick={triggerUpload} data-testid="invoice-intake-upload">
            {t('invoices:workflow.summary.uploadFile')}
          </Button>
          <Button icon={<FileAddOutlined />} disabled={progressBusy} onClick={() => setEditorOpen(true)} data-testid="invoice-intake-manual">
            {t(`invoices:workflow.summary.${dirty ? 'reopenDetails' : 'manualEntry'}`)}
          </Button>
        </div>
        {filePresent && <Space wrap className="invoice-intake-extra">
          <Button icon={<UploadOutlined />} disabled={progressBusy} onClick={() => void extract()}>{t('invoices:workflow.actions.requestExtraction')}</Button>
        </Space>}
      </div>
    ) : (
      <InvoiceDraftSummary
        draft={draft}
        editable={editable}
        dirty={dirty}
        busy={busy}
        extracting={extracting}
        conflict={conflict}
        filePresent={filePresent}
        issues={issues}
        showApply={showApply}
        sourceCount={sourceIds.length}
        onEdit={() => setEditorOpen(true)}
        onUpload={triggerUpload}
        onRequestExtraction={requestExtraction}
        onDownloadFile={downloadFile}
        onApply={confirmApply}
      />
    )}
    {correctionsPanel(draft)}
    {!editable && persisted && (
      <Space wrap>
        <Button onClick={() => setEditorOpen(true)} data-testid="invoice-view-details">
          {t('invoices:workflow.summary.viewDetails')}
        </Button>
      </Space>
    )}
    {persisted && editable && direction === 'SALES' && preview?.salesAlignment && (
      <InvoiceOrderAlignment scope="sales" value={preview.salesAlignment}
        accepted={alignmentAccepted === preview.salesAlignment.fingerprint}
        disabled={progressBusy || dirty || conflict}
        onAccept={accepted => { approvalKey.current = null; setAlignmentAccepted(accepted ? preview.salesAlignment!.fingerprint : null); }} />
    )}
    {persisted && editable && direction === 'SALES' && preview?.approvalIssue && (
      <Alert type="error" showIcon message={preview.approvalIssue.message} />
    )}
    {persisted && !approval && direction === 'SALES' && preview?.advanceBalance && <InvoiceAdvanceBalance balance={preview.advanceBalance} />}
    {/* An advance / settlement invoice has no quantity coverage: its balance is shown above. */}
    {persisted && !isAmountInvoice(draft) && (
      <div className="invoice-draft-section">
        <span className="invoice-draft-section-label">{t('invoices:workflow.summary.ordersTitle')}</span>
        <Collapse ghost items={[{
          key: 'review',
          label: t('invoices:workflow.summary.reviewTitle'),
          forceRender: true,
          children: preview
            ? <InvoiceApprovalPreview invoice={draft} preview={preview} disabled={!editable || progressBusy || dirty} />
            : previewFailed
              ? <Alert type="warning" message={t('invoices:workflow.page.previewFailed')} action={<Button onClick={() => setPreviewRevision(value => value + 1)}>{t('common:refresh')}</Button>} />
              : <Spin size="small" />,
        }]} />
      </div>
    )}
    {editable && persisted && (
      <div className="invoice-draft-section">
        <Collapse ghost items={[{
          key: 'finalization',
          label: t('invoices:workflow.page.finalization'),
          forceRender: true,
          children: (
            <Space wrap align="end">
              <label className="invoice-draft-period">
                <span>{t('invoices:workflow.page.recognitionMonth')}</span>
                <DatePicker picker="month" format={RECOGNITION_MONTH_FORMAT} aria-label={t('invoices:workflow.page.recognitionMonth')} placeholder={t('invoices:workflow.page.recognitionMonth')} value={draft.settlementPeriod ? dayjs(`${draft.settlementPeriod}-01`) : null} disabled={progressBusy || conflict} onChange={date => choosePeriod(date?.format('YYYY-MM') ?? null)} />
              </label>
              <Popconfirm okText={t('common:yes')} cancelText={t('common:no')} title={t(isAmountInvoice(draft) ? 'invoices:workflow.amountInvoice.approveConfirm' : 'invoices:workflow.page.approveConfirm')} onConfirm={() => void approve()} disabled={progressBusy || conflict}>
                <Button type="primary" loading={busy} disabled={extracting || conflict}>{t(`invoices:workflow.page.${direction === 'PURCHASE' ? (embedded ? 'approvePurchase' : 'approvePurchaseOnly') : 'approveSales'}`)}</Button>
              </Popconfirm>
              {monthMismatch && <Alert type="info" showIcon data-testid="recognition-month-differs" message={t('invoices:workflow.page.recognitionMonthDiffers', monthMismatch)} />}
            </Space>
          ),
        }]} />
      </div>
    )}
    {draft.recognizedSupplierOrderId && <>
      <Alert type="success" showIcon message={t('invoices:workflow.financialApproval.recognized', { month: draft.settlementPeriod ? recognitionMonthLabel(draft.settlementPeriod) : undefined, amount: draft.recognizedNetPLN != null ? formatAmountGrouped(draft.recognizedNetPLN) : undefined })} />
      <div ref={receiptPanel}>
        {receiptNotice && <Alert type="warning" showIcon closable onClose={() => setReceiptNotice(null)} className="invoice-draft-notice" data-testid="receipt-prompt-fallback" message={t('invoices:workflow.receiptPrompt.fallbackTitle')} description={receiptNotice} />}
        <PurchaseInvoiceReceipt key={receiptRevision} invoice={draft} preview={preview} readOnly={!embedded} onChanged={() => { setPreviewRevision(value => value + 1); embedded?.onPersisted(); }} />
      </div>
      {embedded && <PurchaseReceiptPrompt open={receiptPromptOpen} invoice={draft} preview={preview} previewFailed={previewFailed}
        onClose={() => setReceiptPromptOpen(false)} onReceived={refreshReceipt}
        onLinked={() => { setReceiptPromptOpen(false); refreshReceipt(); }} onFallback={receiptFallback} />}
    </>}
    {approval && <Card title={t('invoices:workflow.page.approved')}><Space direction="vertical">
      {approval.orders.filter(order => order.coverageState).map(order => <Typography.Text key={order.id}>{t(`invoices:workflow.page.coverage.${order.coverageState!.toLowerCase()}`)}</Typography.Text>)}
      {approval.advanceBalance && <InvoiceAdvanceBalance balance={approval.advanceBalance} />}
    </Space></Card>}
    </div>
    <InvoiceEditorModal
      inline={Boolean(embedded)}
      open={editorOpen}
      title={draft.originalDocumentNumber || t(`invoices:workflow.page.${direction === 'PURCHASE' ? 'title' : 'salesTitle'}`)}
      dirty={dirty}
      saving={busy}
      saveDisabled={extracting || conflict}
      onSave={() => void handleModalSave()}
      onClose={() => setEditorOpen(false)}
      readOnly={!editable}
    >
      <Collapse ghost defaultActiveKey={sourceIds.length > 0 ? [] : ['orders']} className="invoice-draft-modal-orders" items={[{
        key: 'orders',
        label: `${t('invoices:workflow.page.sourceOrders')}${sourceIds.length > 0 ? ` · ${sourceIds.length}` : ''}`,
        forceRender: true,
        children: orderPicker,
      }]} />
      <WorkflowInvoiceEditor value={draft} onChange={edit} extractionStatus={draft.extractionStatus} extractionError={draft.extractionError} extractionModel={draft.extractionModel} extractionPending={extracting} extractionStale={dirty && draft.extractionStatus === 'SUCCESS'} onRequestExtraction={requestExtraction} sourceLineCandidates={candidates} sourceLinesLoading={sourcesLoading} readOnly={!editable || busy || conflict} partialCoverage={draft.coverageState === 'PARTIAL'} frozenOrder={draft.status === 'APPROVED'} />
    </InvoiceEditorModal>
    <input data-testid="invoice-source-input" ref={fileInput} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" hidden onChange={event => { const files = Array.from(event.target.files ?? []); event.target.value = ''; void upload(files); }} />
  </Space></div>;
  // Routed page: document card on the object-card primitives (DESIGN.md
  // „Direction since 2026-10-19”). The embedded ZD window above keeps its layout.
  const purchase = direction === 'PURCHASE';
  const proforma = isSalesProforma(draft);
  const files = draft.sourceFiles ?? [];
  const legacyFile = draft.sourceFileName && !files.some(file => file.name === draft.sourceFileName) ? draft.sourceFileName : null;
  const review = draft.extractionReview ?? null;
  const reviewTotal = review && review.documentTotal != null ? review.documentTotal : null;
  const actionsDisabled = busy || extracting || conflict;
  const pill = withdrawn
    ? { tone: 'obj-pill--danger', label: t('invoices:workflow.card.status.withdrawn') }
    : editable
      // The saved kind only: a kind changed in the editor is not saved yet.
      ? (proforma && persisted && !dirty
        ? { tone: 'obj-pill--success', label: t('invoices:workflow.page.status.saved') }
        : { tone: 'obj-pill--warning', label: t('invoices:workflow.page.status.draft') })
      : draft.status === 'APPROVED'
        ? { tone: 'obj-pill--success', label: t('invoices:workflow.page.status.approved') }
        : { tone: '', label: t(`invoices:workflow.page.status.${draft.status === 'CANCELLED' ? 'cancelled' : 'unknown'}`) };
  const deleteDraft = () => modal.confirm({
    title: t('invoices:workflow.page.deleteConfirm'), okText: t('common:yes'), cancelText: t('common:no'), okButtonProps: { danger: true },
    onOk: () => commands.remove(draft.id!).then(() => navigate(base)).catch(error),
  });
  const moreItems: MenuProps['items'] = [
    ...files.map(file => ({ key: `file:${file.sha256}`, icon: <DownloadOutlined />, label: t('invoices:workflow.card.download', { name: file.name }) })),
    ...(editable && persisted ? [{ key: 'delete', icon: <DeleteOutlined />, danger: true, disabled: progressBusy, label: t('invoices:workflow.page.deleteDraft') }] : []),
  ];
  const coverageLines = approval?.orders.filter(order => order.coverageState) ?? [];
  const recognized = Boolean(draft.recognizedSupplierOrderId);
  const alerts = [
    conflict && <Alert key="conflict" type="error" showIcon message={t('invoices:workflow.api.versionConflict')} description={t('invoices:workflow.page.conflictRetained')} action={<Button onClick={() => modal.confirm({ title: t('invoices:workflow.page.discardConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: () => { void commands.fetch(draft.id!).then(value => { replace(value); setDirty(false); setConflict(false); }).catch(error); } })}>{t('common:refresh')}</Button>} />,
    editable && persisted && draft.extractionStatus === 'FAILED' && <Alert key="ocr-failed" type="error" showIcon message={t('invoices:workflow.extractionFailed')} description={t('invoices:workflow.manualFallback')} />,
    editable && persisted && issues.length > 0 && <Alert key="issues" type="warning" showIcon role="alert" message={t('invoices:workflow.issuesTitle')}
      description={<ul className="invoice-card__issues">{issues.map(issue => <li key={`${issue.field}:${issue.key}`}><Button type="link" onClick={() => setEditorOpen(true)} disabled={busy}>{t(issue.key)}</Button></li>)}</ul>} />,
    reviewTotal !== null && <Alert key="ocr-total" type="info" showIcon
      message={<>{t('invoices:workflow.extractionReviewTotalLabel')}: <span className="doc-amount" data-testid="invoice-summary-ocr-total">{review!.currency ? formatCurrency(reviewTotal, review!.currency) : formatAmountGrouped(reviewTotal)}</span></>}
      description={t('invoices:workflow.summary.ocrUnclassified')} />,
    editable && persisted && direction === 'SALES' && !proforma && preview?.approvalIssue && <Alert key="approval-issue" type="error" showIcon message={preview.approvalIssue.message} />,
    sameNumberAlert,
    !withdrawn && (recognized || coverageLines.length > 0) && <Alert key="approved" type="success" showIcon
      message={[
        recognized
          ? t('invoices:workflow.financialApproval.recognized', { month: draft.settlementPeriod ? recognitionMonthLabel(draft.settlementPeriod) : undefined, amount: draft.recognizedNetPLN != null ? formatAmountGrouped(draft.recognizedNetPLN) : undefined })
          : t('invoices:workflow.page.approved'),
        ...coverageLines.map(order => t(`invoices:workflow.page.coverage.${order.coverageState!.toLowerCase()}`)),
      ].join(' · ')} />,
    receiptNotice && <Alert key="receipt-notice" type="warning" showIcon closable onClose={() => setReceiptNotice(null)} data-testid="receipt-prompt-fallback" message={t('invoices:workflow.receiptPrompt.fallbackTitle')} description={receiptNotice} />,
  ].filter(Boolean);
  const correction = Boolean(draft.correctsInvoiceId) || draft.kind === 'CORRECTION';
  const counterpartyName = purchase ? draft.sellerName : draft.buyerName;
  const counterpartyTaxId = purchase ? draft.sellerTaxId : draft.buyerTaxId;
  const showCorrections = Boolean(draft.id && (draft.status === 'APPROVED' || draft.correctsInvoiceId));
  // Pair corrections | receipt when both exist; a lone section spans the row (symmetry).
  const pairBottom = showCorrections && recognized;
  const heroActions = persisted ? <>
    {moreItems.length > 0 && <Dropdown trigger={['click']} menu={{ items: moreItems, onClick: ({ key }) => { if (key === 'delete') deleteDraft(); else if (key.startsWith('file:')) downloadFile(key.slice(5)); } }}>
      <Button icon={<MoreOutlined />} data-testid="invoice-more-actions">{t('invoices:workflow.card.more')}</Button>
    </Dropdown>}
    {editable
      ? <Button type="primary" icon={<EditOutlined />} onClick={() => setEditorOpen(true)} disabled={busy} data-testid="invoice-edit-details">
        {dirty ? t('invoices:workflow.summary.reopenDetails') : t('invoices:workflow.summary.editDetails')}
      </Button>
      : <Button type="primary" icon={<EyeOutlined />} onClick={() => setEditorOpen(true)} data-testid="invoice-view-details">
        {t('invoices:workflow.summary.viewDetails')}
      </Button>}
  </> : undefined;
  return <div className="obj-card doc-card invoice-card" data-testid="invoice-card">
    <ObjectCardHero
      testId={persisted ? 'invoice-summary' : 'invoice-card-header'}
      kind={t(`invoices:workflow.card.kind.${correction ? 'correction' : 'invoice'}${purchase ? 'Purchase' : 'Sales'}`)}
      name={persisted ? (draft.originalDocumentNumber || t('invoices:workflow.corrections.noNumber')) : t(`invoices:workflow.card.new${purchase ? 'Purchase' : 'Sales'}`)}
      avatar={<FileTextOutlined />}
      backLabel={t(`invoices:workflow.page.${purchase ? 'title' : 'salesTitle'}`)}
      onBack={close}
      badges={<>
        <span className={`obj-pill ${pill.tone}`} data-testid="invoice-status-pill">{pill.label}</span>
        {!correction && draft.kind !== 'STANDARD' && <span className="obj-pill obj-pill--primary obj-pill--plain">{t(`invoices:workflow.kind.${draft.kind.toLowerCase()}`)}</span>}
        {editable && persisted && <span className="obj-pill obj-pill--plain" role="status">
          {t(`invoices:workflow.extraction.${extracting ? 'pending' : draft.extractionStatus.toLowerCase()}`)}
        </span>}
        {editable && draft.manuallyCorrected && <span className="obj-pill obj-pill--warning obj-pill--plain">{t('invoices:workflow.manuallyCorrected')}</span>}
        {dirty && persisted && <span className="obj-pill obj-pill--warning" data-testid="invoice-summary-unsaved">{t('invoices:workflow.summary.unsaved')}</span>}
      </>}
      meta={persisted ? [
        <><ShopOutlined aria-hidden="true" />{counterpartyName ?? t('invoices:workflow.summary.unknownCounterparty')}</>,
        <><IdcardOutlined aria-hidden="true" />{counterpartyTaxId ? `${t('invoices:workflow.card.taxId')} ${counterpartyTaxId}` : t('invoices:workflow.summary.unknownTaxId')}</>,
      ] : undefined}
      actions={heroActions}
      facts={persisted ? invoiceCardFacts(draft, t) : undefined}
      alerts={alerts.length > 0 ? alerts : undefined}
    />
    <div className="obj-card__body">
      {!persisted && <ObjectCardSection wide testId="invoice-intake" tone="blue" icon={<UploadOutlined />}
        title={t('invoices:workflow.summary.intakeTitle')}
        actions={dirty ? <span className="obj-pill obj-pill--warning" data-testid="invoice-intake-unsaved">{t('invoices:workflow.summary.unsaved')}</span> : undefined}>
        <ObjectCardEmpty icon={<FileTextOutlined />} text={t('invoices:workflow.summary.intakeHint')} action={<div className="invoice-card__buttons">
          <Button icon={<FileAddOutlined />} disabled={progressBusy} onClick={() => setEditorOpen(true)} data-testid="invoice-intake-manual">
            {t(`invoices:workflow.summary.${dirty ? 'reopenDetails' : 'manualEntry'}`)}
          </Button>
          {filePresent && <Button icon={<ScanOutlined />} disabled={progressBusy} onClick={() => void extract()}>{t('invoices:workflow.actions.requestExtraction')}</Button>}
          <Button type="primary" icon={<UploadOutlined />} disabled={progressBusy} onClick={triggerUpload} data-testid="invoice-intake-upload">
            {t('invoices:workflow.summary.uploadFile')}
          </Button>
        </div>} />
      </ObjectCardSection>}
      {persisted && <InvoiceCardLines draft={draft} />}
      {persisted && editable && direction === 'SALES' && !proforma && preview?.salesAlignment && <ObjectCardSection wide tone="orange" icon={<SwapOutlined />} title={t('invoices:workflow.card.alignmentSection')} className="invoice-card__alignment">
        <InvoiceOrderAlignment scope="sales" value={preview.salesAlignment}
          accepted={alignmentAccepted === preview.salesAlignment.fingerprint}
          disabled={progressBusy || dirty || conflict}
          onAccept={accepted => { approvalKey.current = null; setAlignmentAccepted(accepted ? preview.salesAlignment!.fingerprint : null); }} />
      </ObjectCardSection>}
      {/* Advance / settlement invoice: no quantity coverage — the ZK balance instead (decision 2026-10-20). */}
      {persisted && direction === 'SALES' && isAmountInvoice(draft) && preview?.advanceBalance && <ObjectCardSection wide tone="teal" icon={<FileSearchOutlined />}
        testId="invoice-advance-balance-section" title={t('invoices:workflow.amountInvoice.balanceTitle')}>
        <InvoiceAdvanceBalance balance={preview.advanceBalance} />
      </ObjectCardSection>}
      {persisted && !isAmountInvoice(draft) && !proforma && (preview
        ? <InvoiceApprovalPreview section invoice={draft} preview={preview} disabled={!editable || progressBusy || dirty} />
        : <ObjectCardSection wide tone="teal" icon={<FileSearchOutlined />} title={t('invoices:workflow.page.coverageTitle')}>
          {previewFailed
            ? <Alert type="warning" message={t('invoices:workflow.page.previewFailed')} action={<Button size="small" onClick={() => setPreviewRevision(value => value + 1)}>{t('common:refresh')}</Button>} />
            : <Spin size="small" />}
        </ObjectCardSection>)}
      {/* A correction draft: its reason (e.g. „Reklamacja”) comes before the approve button (review #408). */}
      {showCorrections && editable && <InvoiceCorrectionsPanel direction={direction} invoice={draft} preview={preview?.correction ?? null}
        onOpen={openInvoice} onChanged={reloadAfterCorrection} locked={dirty || busy || conflict} section={{ wide: true }} />}
      {persisted && editable && <ObjectCardSection testId="invoice-card-files" tone="indigo" icon={<PaperClipOutlined />} title={t('invoices:workflow.summary.attachments')}
        count={files.length + (legacyFile ? 1 : 0)}>
        {legacyFile || files.length > 0
          ? <dl className="obj-facts">
            {legacyFile && <div className="obj-facts__row"><dt title={legacyFile}>{legacyFile}</dt><dd /></div>}
            {files.map(file => <div className="obj-facts__row" key={file.sha256}>
              <dt className="invoice-card__file-name" title={file.name}>{file.name}</dt>
              <dd><Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => downloadFile(file.sha256)}>{t('invoices:workflow.card.downloadShort')}</Button></dd>
            </div>)}
          </dl>
          : <ObjectCardEmpty icon={<PaperClipOutlined />} text={t('invoices:workflow.page.uploadFirst')} />}
        <div className="invoice-card__buttons">
          <Button icon={<UploadOutlined />} onClick={triggerUpload} disabled={actionsDisabled} data-testid="invoice-upload-file">
            {t('invoices:workflow.actions.uploadSourceFile')}
          </Button>
          <Button icon={<ScanOutlined />} onClick={requestExtraction} disabled={actionsDisabled || !filePresent} loading={extracting} data-testid="invoice-request-extraction">
            {t('invoices:workflow.actions.requestExtraction')}
          </Button>
          {showApply && <Button onClick={confirmApply} disabled={actionsDisabled} data-testid="invoice-apply-extraction">
            {t('invoices:workflow.page.applyExtraction')}
          </Button>}
        </div>
      </ObjectCardSection>}
      {persisted && editable && proforma && <ObjectCardSection testId="invoice-proforma" tone="green" icon={<CheckCircleOutlined />} title={t('invoices:workflow.proforma.title')}>
        {draft.orderId
          ? <Typography.Paragraph>{t('invoices:workflow.proforma.saved')}</Typography.Paragraph>
          : <Alert type="warning" showIcon message={t('invoices:workflow.proforma.orderRequired')} />}
        <div className="invoice-card__approve">
          {draft.orderId
            ? <Button type="primary" onClick={() => navigate(`/app/orders/${draft.orderId}`)} disabled={dirty} data-testid="invoice-proforma-open-order">{t('invoices:workflow.proforma.openOrder')}</Button>
            : <Button type="primary" onClick={() => setEditorOpen(true)} disabled={busy} data-testid="invoice-proforma-choose-order">{t('invoices:workflow.proforma.chooseOrder')}</Button>}
        </div>
      </ObjectCardSection>}
      {persisted && editable && !proforma && <ObjectCardSection testId="invoice-finalization" tone="green" icon={<CheckCircleOutlined />} title={t('invoices:workflow.page.finalization')}>
        <Form component={false} layout="vertical" colon={false}>
          <div className="obj-form">
            <Form.Item label={t('invoices:workflow.page.recognitionMonth')}>
              <DatePicker className="doc-control" picker="month" format={RECOGNITION_MONTH_FORMAT} aria-label={t('invoices:workflow.page.recognitionMonth')} placeholder={t('invoices:workflow.page.recognitionMonth')} value={draft.settlementPeriod ? dayjs(`${draft.settlementPeriod}-01`) : null} disabled={progressBusy || conflict} onChange={date => choosePeriod(date?.format('YYYY-MM') ?? null)} />
            </Form.Item>
          </div>
        </Form>
        {monthMismatch && <Alert className="invoice-card__month-hint" type="info" showIcon data-testid="recognition-month-differs" message={t('invoices:workflow.page.recognitionMonthDiffers', monthMismatch)} />}
        <div className="invoice-card__approve">
          <Popconfirm okText={t('common:yes')} cancelText={t('common:no')} title={t(isAmountInvoice(draft) ? 'invoices:workflow.amountInvoice.approveConfirm' : 'invoices:workflow.page.approveConfirm')} onConfirm={() => void approve()} disabled={progressBusy || conflict}>
            <Button type="primary" loading={busy} disabled={extracting || conflict}>{t(`invoices:workflow.page.${purchase ? 'approvePurchaseOnly' : 'approveSales'}`)}</Button>
          </Popconfirm>
        </div>
      </ObjectCardSection>}
      {showCorrections && !editable && <InvoiceCorrectionsPanel direction={direction} invoice={draft} preview={preview?.correction ?? null}
        onOpen={openInvoice} onChanged={reloadAfterCorrection} locked={dirty || busy || conflict} section={{ wide: !pairBottom }} />}
      {recognized && <PurchaseInvoiceReceipt key={receiptRevision} invoice={draft} preview={preview} readOnly section={{ wide: !pairBottom }} onChanged={() => { setPreviewRevision(value => value + 1); }} />}
    </div>
    <InvoiceEditorModal
      inline={Boolean(embedded)}
      open={editorOpen}
      title={draft.originalDocumentNumber || t(`invoices:workflow.page.${direction === 'PURCHASE' ? 'title' : 'salesTitle'}`)}
      dirty={dirty}
      saving={busy}
      saveDisabled={extracting || conflict}
      onSave={() => void handleModalSave()}
      onClose={() => setEditorOpen(false)}
      readOnly={!editable}
    >
      <Collapse ghost defaultActiveKey={sourceIds.length > 0 ? [] : ['orders']} className="invoice-draft-modal-orders" items={[{
        key: 'orders',
        label: `${t('invoices:workflow.page.sourceOrders')}${sourceIds.length > 0 ? ` · ${sourceIds.length}` : ''}`,
        forceRender: true,
        children: orderPicker,
      }]} />
      <WorkflowInvoiceEditor value={draft} onChange={edit} extractionStatus={draft.extractionStatus} extractionError={draft.extractionError} extractionModel={draft.extractionModel} extractionPending={extracting} extractionStale={dirty && draft.extractionStatus === 'SUCCESS'} onRequestExtraction={requestExtraction} sourceLineCandidates={candidates} sourceLinesLoading={sourcesLoading} readOnly={!editable || busy || conflict} partialCoverage={draft.coverageState === 'PARTIAL'} frozenOrder={draft.status === 'APPROVED'} />
    </InvoiceEditorModal>
    <input data-testid="invoice-source-input" ref={fileInput} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" hidden onChange={event => { const files = Array.from(event.target.files ?? []); event.target.value = ''; void upload(files); }} />
  </div>;
}
